// Read-only capacity signals (drying, kiln, workflow bottlenecks)
// Uses heuristics only; does NOT mutate data or touch Supabase.

const DEFAULTS = {
  dryingSlots: 12,      // rough rack slots available
  kilnSlots: 8,         // rough kiln loads available in horizon
  horizonDays: 7,
};

const toDate = (v) => {
  const d = v ? new Date(v) : null;
  return Number.isFinite(d?.getTime?.()) ? d : null;
};

const daysBetween = (a, b) => (b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24);

function sliceByStage(tasks = [], stage) {
  return tasks.filter((t) => (t.stage || t.status) === stage);
}

function upcomingWithin(tasks = [], horizonDays = 7, now = new Date()) {
  return tasks.filter((t) => {
    const end = toDate(t.dueDate || t.end || t.deadline);
    if (!end) return false;
    const diff = daysBetween(now, end);
    return diff >= 0 && diff <= horizonDays;
  });
}

export function estimateDryingLoad(tasks = [], opts = {}) {
  const { dryingSlots = DEFAULTS.dryingSlots, horizonDays = DEFAULTS.horizonDays } = opts || {};
  const drying = upcomingWithin(sliceByStage(tasks, "drying"), horizonDays);
  const load = drying.length;
  const utilizationPct = dryingSlots > 0 ? load / dryingSlots : 0;
  let level = "light";
  if (utilizationPct >= 1.1) level = "over";
  else if (utilizationPct >= 0.95) level = "near";
  else if (utilizationPct >= 0.75) level = "high";
  else if (utilizationPct >= 0.5) level = "medium";

  return { level, load, capacity: dryingSlots, utilizationPct };
}

export function estimateKilnLoad(tasks = [], opts = {}) {
  const { kilnSlots = DEFAULTS.kilnSlots, horizonDays = DEFAULTS.horizonDays } = opts || {};
  const firingStages = ["bisque", "glazeFiring", "glaze", "kiln"];
  const firings = upcomingWithin(
    tasks.filter((t) => firingStages.includes(t.stage || t.status || "")),
    horizonDays
  );
  const load = firings.length;
  const utilizationPct = kilnSlots > 0 ? load / kilnSlots : 0;
  let level = "light";
  if (utilizationPct >= 1.1) level = "over";
  else if (utilizationPct >= 0.95) level = "near";
  else if (utilizationPct >= 0.75) level = "high";
  else if (utilizationPct >= 0.5) level = "medium";

  return { level, load, capacity: kilnSlots, utilizationPct };
}

export function buildCapacitySignals({ tasks = [], capacity = {}, horizonDays = DEFAULTS.horizonDays } = {}) {
  const alerts = [];
  const dryingSlots = capacity?.dryingSlots ?? capacity?.dryingCapacity ?? DEFAULTS.dryingSlots;
  const kilnSlots = capacity?.kilnSlots ?? capacity?.kilnCapacity ?? DEFAULTS.kilnSlots;

  const drying = estimateDryingLoad(tasks, { dryingSlots, horizonDays });
  if (drying.level === "near" || drying.level === "over" || drying.level === "high") {
    alerts.push({
      id: `capacity-drying-${Date.now()}`,
      severity: "warning",
      source: "planner",
      title: "Drying racks filling up",
      message: `About ${Math.round((drying.utilizationPct || 0) * 100)}% of drying capacity in next ${horizonDays} days.`,
      entityType: "capacity",
      createdAt: new Date().toISOString(),
      read: false,
    });
  }

  const kiln = estimateKilnLoad(tasks, { kilnSlots, horizonDays });
  if (kiln.level === "near" || kiln.level === "over" || kiln.level === "high") {
    alerts.push({
      id: `capacity-kiln-${Date.now()}`,
      severity: "warning",
      source: "planner",
      title: "Kiln load building",
      message: `About ${Math.round((kiln.utilizationPct || 0) * 100)}% of kiln slots used in next ${horizonDays} days.`,
      entityType: "capacity",
      createdAt: new Date().toISOString(),
      read: false,
    });
  }

  return alerts;
}
